import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Building2,
  Users,
  Briefcase,
  Calendar,
  CreditCard,
  Mail,
  LayoutDashboard,
  Building,
  UserCheck,
  Settings,
  Bell,
  User,
  ChevronDown,
  ChevronRight,
  Shield,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  subItems?: NavItem[];
}

const navItems: NavItem[] = [
  { title: 'Dashboard', href: '/system-admin', icon: LayoutDashboard },
  { title: 'Tenants', href: '/system-admin/tenants', icon: Building2 },
  { title: 'Users', href: '/system-admin/users', icon: Users },
  { title: 'Companies', href: '/system-admin/companies', icon: Building },
  { title: 'Positions', href: '/system-admin/positions', icon: Briefcase },
  {
    title: 'Candidates',
    href: '/system-admin/candidates',
    icon: UserCheck,
    subItems: [
      { title: 'All Candidates', href: '/system-admin/candidates', icon: UserCheck },
      { title: 'Blocked Emails', href: '/system-admin/candidates/blocked-emails', icon: Shield },
    ],
  },
  { title: 'Interviews', href: '/system-admin/interviews', icon: Calendar },
  { title: 'Billing', href: '/system-admin/billing', icon: CreditCard },
  { title: 'Invitations', href: '/system-admin/invitations', icon: Mail },
  { title: 'Notifications', href: '/system-admin/notifications', icon: Bell },
  { title: 'Profile', href: '/system-admin/profile', icon: User },
  { title: 'Settings', href: '/system-admin/settings', icon: Settings },
];

export const SystemAdminSidebar: React.FC = () => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>(() => {
    // Auto-expand candidates section if we're on a candidates-related page
    if (location.pathname.startsWith('/system-admin/candidates')) {
      return ['/system-admin/candidates'];
    }
    return [];
  });

  const toggleExpanded = (href: string) => {
    setExpandedItems(prev =>
      prev.includes(href) ? prev.filter(item => item !== href) : [...prev, href]
    );
  };

  const renderNavItem = (item: NavItem) => {
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isExpanded = expandedItems.includes(item.href);
    const isCurrentPath = location.pathname === item.href;
    const isChildActive =
      hasSubItems && item.subItems.some(subItem => location.pathname === subItem.href);
    const isActive = isCurrentPath || isChildActive;

    return (
      <li key={item.href}>
        {hasSubItems ? (
          <>
            {/* Parent item with toggle */}
            <button
              onClick={() => toggleExpanded(item.href)}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 relative',
                'hover:bg-slate-800',
                isActive
                  ? 'bg-blue-600/20 text-blue-300 border-l-4 border-blue-400 font-semibold shadow-lg shadow-blue-500/10 ml-1'
                  : 'text-slate-300 hover:text-white'
              )}
            >
              <item.icon className="h-5 w-5 flex-shrink-0" />
              <span className="flex-1 text-left">{item.title}</span>
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>

            {/* Sub-items */}
            {isExpanded && (
              <ul className="ml-4 mt-1 space-y-1">
                {item.subItems.map(subItem => (
                  <li key={subItem.href}>
                    <NavLink
                      to={subItem.href}
                      end={subItem.href === '/system-admin'}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 relative',
                          'hover:bg-slate-800',
                          isActive
                            ? 'bg-blue-600/20 text-blue-300 border-l-4 border-blue-400 font-semibold shadow-lg shadow-blue-500/10 ml-1'
                            : 'text-slate-300 hover:text-white'
                        )
                      }
                    >
                      <subItem.icon className="h-4 w-4 flex-shrink-0" />
                      <span>{subItem.title}</span>
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          /* Regular nav item */
          <NavLink
            to={item.href}
            end={item.href === '/system-admin'}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 relative',
                'hover:bg-slate-800',
                isActive
                  ? 'bg-blue-600/20 text-blue-300 border-l-4 border-blue-400 font-semibold shadow-lg shadow-blue-500/10 ml-1'
                  : 'text-slate-300 hover:text-white'
              )
            }
          >
            <item.icon className="h-5 w-5 flex-shrink-0" />
            <span>{item.title}</span>
          </NavLink>
        )}
      </li>
    );
  };

  return (
    <div className="w-64 bg-slate-900 text-white flex flex-col">
      <div className="p-6">
        <h2 className="text-2xl font-bold">System Admin</h2>
        <p className="text-sm text-slate-400 mt-1">InterviewAI Control Panel</p>
      </div>

      <nav className="flex-1 px-4 pb-4">
        <ul className="space-y-1">{navItems.map(renderNavItem)}</ul>
      </nav>

      <div className="px-4 pb-4">
        <div className="flex items-center justify-center text-xs text-slate-400">
          <span>v{__APP_VERSION__}</span>
        </div>
      </div>
    </div>
  );
};
